import React, { useEffect } from "react";
import "./AdminRehabView.scss";
import { useState } from "react";
import { IRehabSummary, IRehabSummaryItem, IUser } from "../../types/api";
import { formatDateToSwedish } from "../../shared/utility";
import { formatToIsoDate } from "../../shared/dateUtils";
import { formatDate, isBefore, set, startOfDay } from "date-fns";
import AdminRehabSlotPopup from "../../containers/Rehab/AdminRehabSlotPopup/AdminRehabSlotPopup";
import EditRehabSlotPopup from "../../containers/Rehab/EditRehabSlotPopup/EditRehabSlotPopup";
import { toast } from "react-toastify";
import Spinner from "../../components/UI/Spinner/Spinner";
import EditMarkedRehabSlotsPopup from "../../containers/Rehab/EditMarkedRehabSlotsPopup/EditMarkedRehabSlotsPopup";

interface IAdminRehabViewProps {
  rehabIsActive: boolean;
  searchString: string;
  setFilterDate: (date: Date) => void;
  filterDate: Date;
  weekFilterDate: Date;
  rehabSummaryWeekly: IRehabSummary[];
  loading: boolean;
  selectedUser?: IUser;
  setSelectedUser: (user: IUser | undefined) => void;
  openChooseUserPopup: () => void;
  refetchRehabSummary: () => void;
  handleRehabHistoryClick: (slot: IRehabSummaryItem, date: Date) => void;
  isSelectingForHistory: boolean;
  isSelectingForEdit: boolean;
  isSelectingRehabSlots: boolean;
  setIsSelectingRehabSlots: (value: boolean) => void;
  setIsSelectingForEdit: (value: boolean) => void;
  setMarkedRehabSlotsAreReadyForEdit: (value: boolean) => void;
  showEditMarkedRehabSlots: boolean;
  setShowEditMarkedRehabSlots?: (value: boolean) => void;
}

function AdminRehabView(props: IAdminRehabViewProps) {
  const {
    rehabSummaryWeekly,
    loading,
    selectedUser,
    openChooseUserPopup,
    refetchRehabSummary,
    isSelectingForHistory,
    isSelectingForEdit,
    isSelectingRehabSlots,
    setSelectedUser,
    showEditMarkedRehabSlots,
    setShowEditMarkedRehabSlots,
    setMarkedRehabSlotsAreReadyForEdit,
    handleRehabHistoryClick,
    setIsSelectingRehabSlots,
    setIsSelectingForEdit,
    filterDate,
    weekFilterDate,
    rehabIsActive,
    setFilterDate,
    searchString,
  } = props;
  const [showAdminRehabSlotPopup, setShowAdminRehabSlotPopup] = useState(false);
  const [showEditRehabSlotPopup, setShowEditRehabSlotPopup] = useState(false);
  const [selectedRehabSlot, setSelectedRehabSlot] =
    useState<IRehabSummaryItem>();
  const [selectedRehabSlotDate, setSelectedRehabSlotDate] = useState<Date>();
  const [selectedRehabSlots, setSelectedRehabSlots] = useState<
    {
      date: Date;
      start_time: string;
      end_time: string;
    }[]
  >([]);
  const getEndTime = (startTime: string) => {
    const hours = parseInt(startTime.slice(0, 2));
    const minutes = parseInt(startTime.slice(3, 5));

    const date = new Date();
    date.setHours(hours);
    date.setMinutes(minutes);

    date.setMinutes(date.getMinutes() + 15);

    const newHours = date.getHours().toString().padStart(2, "0");
    const newMinutes = date.getMinutes().toString().padStart(2, "0");

    return `${newHours}:${newMinutes}`;
  };

  const handleBookedSlotClick = (slot: IRehabSummaryItem, date: Date) => {
    setSelectedRehabSlot(slot);
    setSelectedRehabSlotDate(date);
    setShowAdminRehabSlotPopup(true);
  };
  const handleSlotClick = (slot: IRehabSummaryItem, date: Date) => {
    if (slot.bookable === false) {
      toast.error(
        "Denna tid är inte bokningsbar. Redigera om du vill ändra bokningsbarheten."
      );
      return;
    }
    setSelectedRehabSlot(slot);
    setSelectedRehabSlotDate(date);
    if (selectedUser) {
      setShowAdminRehabSlotPopup(true);
    } else {
      openChooseUserPopup();
    }
  };
  useEffect(() => {
    if (selectedRehabSlot && selectedRehabSlotDate && selectedUser) {
      setShowAdminRehabSlotPopup(true);
    }
  }, [selectedUser]);

  useEffect(() => {
    if (!rehabIsActive) return;

    // Compared as calendar days rather than timestamps. weekFilterDate is
    // midnight, so week end + 6 days landed on Sunday 00:00 — any Sunday
    // afternoon then looked like it fell outside the week and the view snapped
    // back to Monday on every switch to the rehab tab.
    const end = new Date(weekFilterDate);
    end.setDate(end.getDate() + 6);

    const weekStart = formatDate(weekFilterDate, "yyyy-MM-dd");
    const weekEnd = formatDate(end, "yyyy-MM-dd");
    const current = formatDate(filterDate, "yyyy-MM-dd");

    if (current < weekStart || current > weekEnd) {
      setFilterDate(new Date(weekFilterDate));
    }
  }, [rehabIsActive, weekFilterDate]);

  useEffect(() => {
    if (!isSelectingRehabSlots) {
      setSelectedRehabSlots([]);
    }
  }, [isSelectingRehabSlots]);

  useEffect(() => {
    if (selectedRehabSlots.length > 1) {
      setMarkedRehabSlotsAreReadyForEdit(true);
    } else {
      setMarkedRehabSlotsAreReadyForEdit(false);
    }
  }, [selectedRehabSlots]);

  const generateWeeklySlots = () => {
    if (!rehabSummaryWeekly && !loading) return null;

    return (
      <tr>
        {rehabSummaryWeekly.map((rehabSummary, index) => {
          const now = new Date();
          const date = new Date(rehabSummary.date);
          const currentDateTime = new Date();
          const currentDateStart = startOfDay(currentDateTime);
          const filterDateStart = startOfDay(now);
          const highlightClass =
            formatDate(filterDate, "yyyy-MM-dd") ===
            formatDate(date, "yyyy-MM-dd")
              ? "highlighted-date"
              : "";

          return (
            <td key={index} className={`rehab-cell ${highlightClass}`}>
              {rehabSummary.slots.length === 0 && (
                <div className="rehab-item-wrapper in-past-unbooked">
                  <div className="rehab-item-content-wrapper"></div>
                </div>
              )}
              {rehabSummary.slots.map((slot, slotIndex) => {
                let matchesSearchString = false;
                const startTime = slot.start_time.slice(0, 5);
                const endTime = getEndTime(slot.start_time);
                const isBooked = slot.booking !== null;
                const hasComment = slot.comment && slot.comment.length > 0;
                const slotTimeHours = parseInt(slot.start_time.slice(0, 2));
                const slotTimeMinutes = parseInt(slot.start_time.slice(3, 5));
                const isBookable =
                  slot.bookable === true || slot.bookable === null;
                const booking = slot.booking || {};
                // Convert slot time to total minutes for easier comparison
                const slotTotalMinutes = slotTimeHours * 60 + slotTimeMinutes;
                const slotEndTime = new Date(
                  `${formatDate(date, "yyyy-MM-dd")}T${endTime}`
                );
                const isInPast =
                  isBefore(slotEndTime, currentDateTime) ||
                  isBefore(filterDateStart, currentDateStart);
                const name = isBooked ? slot.booking.integration_id : "";

                if (searchString && isBooked) {
                  matchesSearchString =
                    booking.member_id.includes(searchString) ||
                    booking.integration_id
                      .toLowerCase()
                      .includes(searchString.toLowerCase());
                }

                const matchesClass = matchesSearchString
                  ? "matches-search"
                  : searchString.length > 0
                  ? "no-match"
                  : "";

                const isSelectedClass = selectedRehabSlots.some(
                  (selectedSlot) =>
                    selectedSlot.start_time === slot.start_time &&
                    selectedSlot.date.toDateString() === date.toDateString()
                )
                  ? "highlight-selected"
                  : "";

                let isInIntervalClass = "";
                if (selectedRehabSlots.length > 1) {
                  // Get all slots selected for this date
                  const slotsForThisDate = selectedRehabSlots.filter(
                    (selectedSlot) =>
                      selectedSlot.date.toDateString() === date.toDateString()
                  );

                  if (slotsForThisDate.length > 0) {
                    // Find earliest and latest selected slots
                    const earliestTime = slotsForThisDate.reduce(
                      (earliest, slot) =>
                        slot.start_time < earliest ? slot.start_time : earliest,
                      slotsForThisDate[0].start_time
                    );

                    const latestTime = slotsForThisDate.reduce(
                      (latest, slot) =>
                        slot.start_time > latest ? slot.start_time : latest,
                      slotsForThisDate[0].start_time
                    );

                    // Check if current slot is within the interval
                    if (
                      slot.start_time >= earliestTime &&
                      slot.start_time <= latestTime
                    ) {
                      isInIntervalClass = "highlight-interval";
                    }
                  }
                }

                return (
                  <div
                    key={`slot-${index}-${slotIndex}`}
                    className={`rehab-item-wrapper text-m ${
                      isInPast && !isBooked
                        ? "in-past-unbooked"
                        : "is-available"
                    } ${isBooked ? "slot-booked" : ""} ${
                      !isBookable && "not-bookable"
                    } ${matchesClass} ${isSelectedClass} ${isInIntervalClass}`}
                    onClick={() => {
                      if (isSelectingRehabSlots) {
                        setSelectedRehabSlots((prevSelectedSlots) => {
                          const isSlotSelected = prevSelectedSlots.some(
                            (selectedSlot) =>
                              selectedSlot.start_time === slot.start_time &&
                              selectedSlot.date.toDateString() ===
                                date.toDateString()
                          );
                          if (isSlotSelected) {
                            return prevSelectedSlots.filter(
                              (selectedSlot) =>
                                selectedSlot.start_time !== slot.start_time ||
                                selectedSlot.date.toDateString() !==
                                  date.toDateString()
                            );
                          } else {
                            return [
                              ...prevSelectedSlots,
                              {
                                start_time: slot.start_time,
                                end_time: slot.end_time,
                                date: date,
                              },
                            ];
                          }
                        });
                        return;
                      }
                      if (isSelectingForHistory) {
                        handleRehabHistoryClick(slot, date);
                        return;
                      }
                      if (isSelectingForEdit) {
                        setSelectedRehabSlotDate(date);
                        setSelectedRehabSlot(slot);
                        setShowEditRehabSlotPopup(true);

                        return;
                      }

                      if (isInPast && !isBooked) return;
                      if (isBooked) {
                        handleBookedSlotClick(slot, date);
                      } else {
                        handleSlotClick(slot, date);
                      }
                    }}
                  >
                    {hasComment && (
                      <i className="fa-regular fa-circle-info comment-icon"></i>
                    )}
                    <div className={`rehab-item-content-wrapper  `}>
                      {startTime} - {endTime}
                      {isBooked && (
                        <div className="rehab-item-booked-wrapper">
                          <span className="rehab-item-booked-text">{name}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </td>
          );
        })}
      </tr>
      //   <tr>

      //     {rehabSummaryWeekly.map((day, index) => {
      //       const date = new Date(day.date);
      //       const { weekday } = formatDateToSwedish(date);

      //       return (
      //         <>
      //           {day.slots.map((slot, slotIndex) => (
      //             <td key={index}>
      //               <div className="rehab-item-wrapper">
      //                 <div key={slotIndex} className="rehab-item-content-wrapper">
      //                   {slot.start_time} - {slot.end_time}
      //                 </div>
      //               </div>
      //             </td>
      //           ))}
      //         </>
      //       );
      //     })}
      //   </tr>
    );
  };
  if (!rehabSummaryWeekly) {
    return <Spinner />;
  }
  return (
    <>
      <div className="rehab-table-overflow-wrapper">
        <table className="rehab-table">
          <thead>
            <tr>
              {rehabSummaryWeekly.map((day, index) => {
                const date = new Date(day.date);
                const { weekday } = formatDateToSwedish(date);
                const highlightClass =
                  filterDate === date ? "highlighted-date" : "";
                return (
                  <th key={index} className="text-s secondary-text">
                    {weekday} - {formatToIsoDate(date).slice(8, 10)}/
                    {formatToIsoDate(date).slice(5, 7)}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="rehab-table-body">{generateWeeklySlots()}</tbody>
        </table>
      </div>
      <AdminRehabSlotPopup
        showPopup={showAdminRehabSlotPopup}
        onClose={() => {
          setShowAdminRehabSlotPopup(false);
          setSelectedRehabSlot(undefined);
        }}
        onConfirm={() => {
          setSelectedUser(undefined);
          refetchRehabSummary();
        }}
        rehabSummaryItem={selectedRehabSlot}
        date={selectedRehabSlotDate || new Date()}
        selectedUser={selectedUser}
      />
      <EditRehabSlotPopup
        showPopup={showEditRehabSlotPopup}
        onClose={() => {
          setShowEditRehabSlotPopup(false);
          setSelectedRehabSlot(undefined);
          setIsSelectingForEdit(false);
        }}
        onConfirm={() => {
          refetchRehabSummary();
        }}
        rehabSummaryItem={selectedRehabSlot}
        date={selectedRehabSlotDate || new Date()}
      />
      <EditMarkedRehabSlotsPopup
        showPopup={showEditMarkedRehabSlots}
        onClose={() => {
          setShowEditMarkedRehabSlots?.(false);
          setSelectedRehabSlots([]);
          setMarkedRehabSlotsAreReadyForEdit(false);
          setIsSelectingRehabSlots(false);
          setSelectedRehabSlot(undefined);
        }}
        onConfirm={() => {
          refetchRehabSummary();
        }}
        selectedRehabSlots={selectedRehabSlots}
      />
    </>
  );
}

export default AdminRehabView;
